// V3.3 email-context: state for the EmailThreadDrawer opened from an
// email_message chip. Holds the drawer open flag, the active email thread id,
// and a per-thread content cache so re-opening the same thread never refetches.
//
// Reads pick their endpoint from the page context up front, via
// selectSharedChatThreadId: the shared chat page reads through the routes
// nested under its chat thread id, everything else through the owner routes.
// There is deliberately no owner->shared fallback on 404 — see
// SharedChatOption in src/services/emailContextApi.ts.
//
// 401/403/404 translate into a "not_available" status; the drawer surfaces that
// as an in-shelf notice. On the shared path a 404 is the normal outcome for a
// chat that is no longer shared, or an email that was unattached, quarantined,
// or deleted — deliberately indistinguishable, and not retried.
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getEmailThreadDetail,
  getEmailContextSettings,
  listEmailThreads,
  downloadEmailAttachment,
  getEmailAttachmentsStatus,
  EmailThreadDetail,
  EmailContextSettings,
  EmailThreadSummary,
  EmailThreadAttachmentsStatus,
  EmailMessageDetail,
} from "src/services/emailContextApi";

export type EmailThreadStatus = "loading" | "loaded" | "error" | "not_available";
export type FetchStatus = "idle" | "loading" | "loaded" | "error";
export type DownloadStatus = "downloading" | "unavailable" | undefined;

// Staged-for-attach threads store {id, subject} pairs directly (snapshotted
// at Attach time) rather than bare ids resolved against availableEmailThreads
// — that list gets replaced wholesale on every fetch/refresh, so a live
// lookup could silently blank out a chip's subject if the staged thread
// later scrolls out of the fetched window.
export interface EmailThreadSelection {
  id: string;
  subject: string;
}

interface EmailThreadEntry {
  status: EmailThreadStatus;
  detail?: EmailThreadDetail;
}

interface EmailContextState {
  open: boolean;
  activeEmailThreadId: string | null;
  activeCutoffTimestamp: string | null;
  byEmailThreadId: Record<string, EmailThreadEntry>;
  settings: EmailContextSettings | null;
  settingsStatus: FetchStatus;
  settingsError: string | null;
  availableEmailThreads: EmailThreadSummary[];
  threadsStatus: FetchStatus;
  threadsError: string | null;
  downloadStatusByAttachmentId: Record<string, DownloadStatus>;
  selectedEmailThreads: EmailThreadSelection[];
  attachmentStatusByThreadId: Record<string, EmailThreadAttachmentsStatus>;
  attachmentsStatusFetchStatus: FetchStatus;
  attachmentsProcessingComplete: boolean;
}

const initialState: EmailContextState = {
  open: false,
  activeEmailThreadId: null,
  activeCutoffTimestamp: null,
  byEmailThreadId: {},
  settings: null,
  settingsStatus: "idle",
  settingsError: null,
  availableEmailThreads: [],
  threadsStatus: "idle",
  threadsError: null,
  downloadStatusByAttachmentId: {},
  selectedEmailThreads: [],
  attachmentStatusByThreadId: {},
  attachmentsStatusFetchStatus: "idle",
  // No polled set yet — vacuously complete so gating never blocks Send
  // before any email thread has actually been attached.
  attachmentsProcessingComplete: true,
};

const NOT_AVAILABLE_STATUSES = [401, 403, 404];

// The chat thread id to read email content through, or null to use the owner
// routes. On the shared chat page this is the same id already in the page URL
// (messageSlice.threadData.id, set by getSecuredSharedMessages) — that nesting is what
// authorizes a non-owner's read. Shared by both read thunks so the branch is
// defined once.
export const selectSharedChatThreadId = (state: any): string | null =>
  state?.homeslice?.isSharedThreadPage ? (state?.messageSlice?.threadData?.id ?? null) : null;

interface FetchRejection {
  emailThreadId: string;
  kind: "not_available" | "error";
}

interface FetchFulfilled {
  emailThreadId: string;
  detail: EmailThreadDetail;
  cached: boolean;
}

export const fetchEmailThreadDetail = createAsyncThunk<
  FetchFulfilled,
  string,
  { rejectValue: FetchRejection }
>("emailContext/fetchEmailThreadDetail", async (emailThreadId, thunkAPI) => {
  const state: any = thunkAPI.getState();
  const cached = state.emailContext?.byEmailThreadId?.[emailThreadId];
  if (cached?.status === "loaded" && cached.detail) {
    // Already fetched this session — reuse, no network call.
    return { emailThreadId, detail: cached.detail, cached: true };
  }

  const accessToken = localStorage.getItem("accessToken2");
  const result = await getEmailThreadDetail(accessToken, emailThreadId, {
    sharedChatThreadId: selectSharedChatThreadId(state),
  });

  if (result.ok) {
    return { emailThreadId, detail: result.data, cached: false };
  }
  const kind = NOT_AVAILABLE_STATUSES.includes(result.status) ? "not_available" : "error";
  return thunkAPI.rejectWithValue({ emailThreadId, kind });
});

// Compose-time: the popover's forwarding address + activity summary.
export const fetchEmailContextSettings = createAsyncThunk<EmailContextSettings, void, { rejectValue: string }>(
  "emailContext/fetchEmailContextSettings",
  async (_arg, thunkAPI) => {
    const accessToken = localStorage.getItem("accessToken2");
    const result = await getEmailContextSettings(accessToken);
    if (result.ok) return result.data;
    return thunkAPI.rejectWithValue(result.error);
  },
);

// Compose-time: the picker's recent-threads list.
export const fetchAvailableEmailThreads = createAsyncThunk<
  EmailThreadSummary[],
  { limit?: number } | void,
  { rejectValue: string }
>("emailContext/fetchAvailableEmailThreads", async (arg, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const result = await listEmailThreads(accessToken, { limit: arg?.limit });
  if (result.ok) return result.data;
  return thunkAPI.rejectWithValue(result.error);
});

interface DownloadEmailAttachmentArgs {
  attachmentId: string;
  filename: string;
}

interface DownloadRejection {
  attachmentId: string;
  kind: "unavailable" | "error";
}

// Resolves the presigned URL and triggers the browser download as a side
// effect (mirrors the click-a-generated-<a> pattern used elsewhere in the
// app for file downloads) — the thunk only tracks per-attachment status so
// the UI can disable the control / show "not yet available" without owning
// its own fetch.
export const downloadEmailAttachmentThunk = createAsyncThunk<
  { attachmentId: string },
  DownloadEmailAttachmentArgs,
  { rejectValue: DownloadRejection }
>("emailContext/downloadEmailAttachment", async ({ attachmentId, filename }, thunkAPI) => {
  const accessToken = localStorage.getItem("accessToken2");
  const result = await downloadEmailAttachment(accessToken, attachmentId, {
    sharedChatThreadId: selectSharedChatThreadId(thunkAPI.getState()),
  });
  if (result.ok) {
    const link = document.createElement("a");
    link.href = result.data.url;
    link.download = result.data.filename || filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return { attachmentId };
  }
  // 403 until virus-scan clean; anything else is a genuine error.
  const kind = result.status === 403 ? "unavailable" : "error";
  return thunkAPI.rejectWithValue({ attachmentId, kind });
});

// Polls extraction status for the given (currently attached) email threads.
// Aggregate per-thread status and processing_complete come straight from the
// backend — this thunk does not recompute precedence client-side.
export const fetchEmailAttachmentsStatus = createAsyncThunk<
  { threads: EmailThreadAttachmentsStatus[]; processingComplete: boolean },
  string[],
  { rejectValue: string }
>("emailContext/fetchEmailAttachmentsStatus", async (emailThreadIds, thunkAPI) => {
  if (!emailThreadIds || emailThreadIds.length === 0) {
    return { threads: [], processingComplete: true };
  }
  const accessToken = localStorage.getItem("accessToken2");
  const result = await getEmailAttachmentsStatus(accessToken, emailThreadIds);
  if (result.ok) {
    return { threads: result.data.email_threads, processingComplete: result.data.processing_complete };
  }
  return thunkAPI.rejectWithValue(result.error);
});

const emailContextSlice = createSlice({
  name: "emailContext",
  initialState,
  reducers: {
    openEmailThreadDrawer: (
      state,
      action: PayloadAction<{ emailThreadId: string; messageCreatedAt?: string | null }>,
    ) => {
      state.open = true;
      state.activeEmailThreadId = action.payload.emailThreadId;
      state.activeCutoffTimestamp = action.payload.messageCreatedAt ?? null;
    },
    closeEmailThreadDrawer: (state) => {
      state.open = false;
      state.activeEmailThreadId = null;
      state.activeCutoffTimestamp = null;
    },
    // Re-attaching a thread can mean new emails exist that the cached detail
    // (fetched before this attach) doesn't have — clear it so the next open
    // refetches. See design.md Decision 3 for why this fires at the two
    // attach-submission sites rather than reacting to message-insert actions.
    invalidateEmailThreadCache: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        delete state.byEmailThreadId[id];
      });
    },
    // Compose-time: bulk-commits the picker's checked selection as the
    // staged-for-attach set (fired once, on Attach click — not per-checkbox).
    setSelectedEmailThreads: (state, action: PayloadAction<EmailThreadSelection[]>) => {
      state.selectedEmailThreads = action.payload;
    },
    // Compose-time: drop a single staged thread (chip remove control).
    removeSelectedEmailThread: (state, action: PayloadAction<string>) => {
      state.selectedEmailThreads = state.selectedEmailThreads.filter((t) => t.id !== action.payload);
    },
    // Compose-time: clear the staged set after a successful send.
    clearSelectedEmailThreads: (state) => {
      state.selectedEmailThreads = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmailThreadDetail.pending, (state, action) => {
        const id = action.meta.arg;
        // Preserve a cached "loaded" entry so re-opens render instantly.
        if (state.byEmailThreadId[id]?.status !== "loaded") {
          state.byEmailThreadId[id] = { status: "loading" };
        }
      })
      .addCase(fetchEmailThreadDetail.fulfilled, (state, action) => {
        const { emailThreadId, detail } = action.payload;
        state.byEmailThreadId[emailThreadId] = { status: "loaded", detail };
      })
      .addCase(fetchEmailThreadDetail.rejected, (state, action) => {
        const payload = action.payload;
        const id = payload?.emailThreadId ?? action.meta.arg;
        state.byEmailThreadId[id] = { status: payload?.kind ?? "error" };
      })
      .addCase(fetchEmailContextSettings.pending, (state) => {
        state.settingsStatus = "loading";
        state.settingsError = null;
      })
      .addCase(fetchEmailContextSettings.fulfilled, (state, action) => {
        state.settingsStatus = "loaded";
        state.settings = action.payload;
      })
      .addCase(fetchEmailContextSettings.rejected, (state, action) => {
        state.settingsStatus = "error";
        state.settingsError = action.payload ?? "error";
      })
      .addCase(fetchAvailableEmailThreads.pending, (state) => {
        state.threadsStatus = "loading";
        state.threadsError = null;
      })
      .addCase(fetchAvailableEmailThreads.fulfilled, (state, action) => {
        state.threadsStatus = "loaded";
        state.availableEmailThreads = action.payload;
      })
      .addCase(fetchAvailableEmailThreads.rejected, (state, action) => {
        state.threadsStatus = "error";
        state.threadsError = action.payload ?? "error";
        // Threads-listing failure is non-fatal — the user can still copy the
        // forwarding address and send emails. Fall back to an empty list
        // rather than leaving stale data or blocking the popover.
        state.availableEmailThreads = [];
      })
      .addCase(downloadEmailAttachmentThunk.pending, (state, action) => {
        state.downloadStatusByAttachmentId[action.meta.arg.attachmentId] = "downloading";
      })
      .addCase(downloadEmailAttachmentThunk.fulfilled, (state, action) => {
        delete state.downloadStatusByAttachmentId[action.payload.attachmentId];
      })
      .addCase(downloadEmailAttachmentThunk.rejected, (state, action) => {
        const payload = action.payload;
        const id = payload?.attachmentId ?? action.meta.arg.attachmentId;
        state.downloadStatusByAttachmentId[id] = payload?.kind === "unavailable" ? "unavailable" : undefined;
      })
      .addCase(fetchEmailAttachmentsStatus.pending, (state) => {
        state.attachmentsStatusFetchStatus = "loading";
      })
      .addCase(fetchEmailAttachmentsStatus.fulfilled, (state, action) => {
        state.attachmentsStatusFetchStatus = "loaded";
        // Threads the caller doesn't own are simply absent from the response —
        // merge rather than replace so an unowned id polled once doesn't wipe
        // a previously-known entry for a different, still-valid thread.
        action.payload.threads.forEach((t) => {
          state.attachmentStatusByThreadId[t.email_thread_id] = t;
        });
        state.attachmentsProcessingComplete = action.payload.processingComplete;
      })
      .addCase(fetchEmailAttachmentsStatus.rejected, (state) => {
        state.attachmentsStatusFetchStatus = "error";
      });
  },
});

export const {
  openEmailThreadDrawer,
  closeEmailThreadDrawer,
  invalidateEmailThreadCache,
  setSelectedEmailThreads,
  removeSelectedEmailThread,
  clearSelectedEmailThreads,
} = emailContextSlice.actions;

// Point-in-time view: a message is visible if it existed at or before the
// cutoff (the attaching chip's own created_at). Fails open — no cutoff, or a
// message with neither sent_at nor received_at, is always shown rather than
// hidden — so a missing timestamp degrades to today's "show everything"
// behavior instead of silently hiding content. See design.md Decision 2.
export const selectVisibleEmailMessages = (
  messages: EmailMessageDetail[] | undefined,
  cutoff: string | null,
): EmailMessageDetail[] => {
  if (!messages) return [];
  if (!cutoff) return messages;
  return messages.filter((m) => {
    const timestamp = m.sent_at || m.received_at;
    return !timestamp || timestamp <= cutoff;
  });
};

export const selectStagedEmailThreads = (state: { emailContext: EmailContextState }): EmailThreadSelection[] =>
  state.emailContext.selectedEmailThreads;

export const selectStagedEmailThreadIds = (state: { emailContext: EmailContextState }): string[] =>
  state.emailContext.selectedEmailThreads.map((t) => t.id);

export const selectEmailAttachmentsProcessingComplete = (state: { emailContext: EmailContextState }): boolean =>
  state.emailContext.attachmentsProcessingComplete;

export const selectEmailAttachmentStatusByThreadId = (
  state: { emailContext: EmailContextState },
): Record<string, EmailThreadAttachmentsStatus> => state.emailContext.attachmentStatusByThreadId;

export default emailContextSlice.reducer;
